import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  RotateCcw
} from 'lucide-react';
import { fetchBlogById } from '../lib/firebaseBlog';
import { ShareSidebar } from '../components/ShareSidebar';

const categoryConfig = {
  tech: { title: 'Syntax & Systems' },
  rabbit_hole: { title: 'Rabbit Hole' },
};

function readingTimeMinutes(content) {
  if (!content || typeof content !== 'string') return 1;
  const text = content.replace(/<[^>]+>/g, '');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export function BlogDetailPage() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Audio Player State
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  
  const audioRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      const data = await fetchBlogById(id);
      setBlog(data);
      setLoading(false);
    };
    load();
  }, [id]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [blog?.audioUrl]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const resetAudio = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 1.75, 2];

  const cyclePlaybackRate = () => {
    const currentIndex = SPEED_OPTIONS.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % SPEED_OPTIONS.length;
    const newRate = SPEED_OPTIONS[nextIndex];
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 pt-20">
        <h1 className="font-heading text-4xl text-foreground">Post Not Found</h1>
        <Link to="/blog" className="text-primary hover:underline font-body">
          Back to Blog
        </Link>
      </div>
    );
  }

  const categoryTitle = (categoryConfig[blog.category] || categoryConfig.tech).title;
  const readingTime = readingTimeMinutes(blog.content);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  const isRichContent = /<[^>]+>/.test(blog.content);
  const hasAudio = !!blog.audioUrl;

  return (
    <div className="min-h-screen pt-20 pb-20 flex">
      <ShareSidebar url={shareUrl} title={blog.title} description={blog.excerpt} />

      <article className="flex-1 max-w-3xl mx-auto px-6 md:px-10">
        {/* Metadata */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="flex items-center gap-3 text-sm font-body text-muted-foreground mb-4"
        >
          <span className="uppercase tracking-widest font-bold text-primary">{categoryTitle}</span>
          <span aria-hidden>|</span>
          <span>Reading time: {readingTime} min</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          data-testid="blog-title"
          className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6 leading-tight"
        >
          {blog.title}
        </motion.h1>

        {/* Audio Player - Only show if audio exists */}
        {hasAudio && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="mb-8"
          >
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-body font-semibold text-foreground">Listen to this article</h3>
                  <p className="font-body text-xs text-muted-foreground">AI-powered narration</p>
                </div>
              </div>

              <audio ref={audioRef} src={blog.audioUrl} preload="metadata" />
              
              {/* Progress Bar */}
              <div className="flex items-center gap-3 mb-3">
                <span className="font-mono text-xs text-muted-foreground w-10">
                  {formatTime(currentTime)}
                </span>
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleSeek}
                  className="flex-1 h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                  data-testid="audio-progress"
                />
                <span className="font-mono text-xs text-muted-foreground w-10">
                  {formatTime(duration)}
                </span>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={togglePlayPause}
                    className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors"
                    data-testid="play-pause-button"
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5 ml-0.5" />
                    )}
                  </button>
                  
                  <button
                    onClick={resetAudio}
                    className="p-2.5 text-muted-foreground hover:text-foreground transition-colors"
                    title="Restart"
                  >
                    <RotateCcw className="w-5 h-5" />
                  </button>

                  <button
                    onClick={cyclePlaybackRate}
                    className="px-2.5 py-1 min-w-[3.2rem] text-xs font-mono font-bold bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                    title="Playback speed"
                  >
                    {playbackRate}x
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleMute}
                    className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="mute-button"
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 h-1.5 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-full"
                    data-testid="volume-slider"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          data-testid="blog-content"
          className="font-body text-foreground leading-relaxed"
        >
          {isRichContent ? (
            <div 
              className="prose-custom space-y-6"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(blog.content) 
              }}
            />
          ) : (
            <div className="space-y-6">
              {blog.content.split('\n').filter(line => line.trim()).map((line, i) => {
                const isBlockquote = /^—\s*.+/.test(line) || /^"\s*—\s*.+/.test(line);
                if (isBlockquote) {
                  return (
                    <blockquote
                      key={i}
                      className="pl-4 border-l-2 border-foreground text-muted-foreground italic my-6"
                    >
                      {line.replace(/^["']?\s*—\s*/, '').replace(/["']$/, '')}
                    </blockquote>
                  );
                }
                return <p key={i} className="text-foreground">{line}</p>;
              })}
            </div>
          )}
        </motion.div>

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.25 }}
            className="mt-8 flex flex-wrap gap-2"
          >
            {blog.tags.map(tag => (
              <span
                key={tag}
                className="px-3 py-1 bg-primary/10 text-primary font-body text-sm rounded-full"
              >
                #{tag}
              </span>
            ))}
          </motion.div>
        )}

        {/* Back to blog */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-16 pt-8 border-t border-border"
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primary hover:underline font-body text-sm font-medium"
          >
            Back to Blog
          </Link>
        </motion.footer>
      </article>
    </div>
  );
}
